import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  TRUST_SIGNAL_PRESET_ICON_NAMES,
  TRUST_SIGNAL_PRESET_ICON_URLS,
  TRUST_SIGNALS_MAX_COUNT,
  createDefaultTrustSignal,
} from '../../defaults';
import {
  UploadIconDialogComponent,
  UploadIconDialogResult,
} from '@app/sharedModule/components/upload-icon-dialog/upload-icon-dialog.component';

@Component({
  selector: 'trust-signals-component-editor',
  templateUrl: './trust-signals-component-editor.component.html',
  styleUrls: ['./trust-signals-component-editor.component.scss'],
  standalone: false,
})
export class TrustSignalsComponentEditorComponent implements OnInit {
  @Input() componentForm: UntypedFormGroup;

  optionsForm: UntypedFormGroup;
  signals: UntypedFormArray;

  readonly presetIconUrls = TRUST_SIGNAL_PRESET_ICON_NAMES.map((name) => TRUST_SIGNAL_PRESET_ICON_URLS[name]);
  readonly presetIconLabels = TRUST_SIGNAL_PRESET_ICON_NAMES.map((name) => this.formatPresetLabel(name));
  readonly maxSignals = TRUST_SIGNALS_MAX_COUNT;

  private destroyRef = inject(DestroyRef);

  constructor(private dialog: MatDialog) {}

  ngOnInit(): void {
    this.optionsForm = this.componentForm.get('options') as UntypedFormGroup;
    this.signals = this.optionsForm?.get('signals') as UntypedFormArray;
  }

  get signalsCount(): number {
    return this.signals?.controls?.length ?? 0;
  }

  get canAddSignal(): boolean {
    return this.signalsCount < this.maxSignals;
  }

  getSignalAt(index: number): UntypedFormGroup {
    return this.signals.at(index) as UntypedFormGroup;
  }

  iconUrlValue(signalForm: UntypedFormGroup): string | File {
    return signalForm.get('iconUrl')?.value ?? '';
  }

  customIconUrlValue(signalForm: UntypedFormGroup): string | File {
    return signalForm.get('customIconUrl')?.value ?? '';
  }

  onIconUrlChange(signalForm: UntypedFormGroup, value: string | File): void {
    signalForm.get('iconUrl').setValue(value);
    signalForm.markAsDirty();
    this.signals.markAsDirty();
  }

  onUploadCustomRequested(signalForm: UntypedFormGroup): void {
    this.dialog
      .open<UploadIconDialogComponent, void, UploadIconDialogResult | null>(UploadIconDialogComponent, {
        width: '576px',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;
        signalForm.get('customIconUrl').setValue(result.file);
        signalForm.get('iconUrl').setValue(result.file);
        signalForm.markAsDirty();
        this.signals.markAsDirty();
      });
  }

  onRemoveCustomRequested(signalForm: UntypedFormGroup): void {
    const customValue = signalForm.get('customIconUrl')?.value;
    const iconValue = signalForm.get('iconUrl')?.value;
    const wasSelected = !!customValue && iconValue === customValue;
    signalForm.get('customIconUrl').setValue(null);
    if (wasSelected) {
      signalForm.get('iconUrl').setValue('');
    }
    signalForm.markAsDirty();
    this.signals.markAsDirty();
  }

  addSignal(): void {
    if (!this.canAddSignal) return;
    const signal = createDefaultTrustSignal();
    this.signals.push(
      new UntypedFormGroup({
        id: new UntypedFormControl(signal.id),
        text: new UntypedFormControl(signal.text, [Validators.required]),
        iconUrl: new UntypedFormControl(signal.iconUrl ?? ''),
        customIconUrl: new UntypedFormControl(signal.customIconUrl ?? ''),
      }),
    );
    this.signals.markAsDirty();
  }

  removeSignal(index: number): void {
    if (this.signalsCount <= 1) return;
    this.signals.removeAt(index);
    this.signals.markAsDirty();
  }

  onDrop(event: CdkDragDrop<unknown>): void {
    if (event.previousIndex === event.currentIndex) return;
    const control = this.signals.at(event.previousIndex);
    this.signals.removeAt(event.previousIndex);
    this.signals.insert(event.currentIndex, control);
    this.signals.markAsDirty();
  }

  private formatPresetLabel(name: string): string {
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ');
  }
}
